/**
 * Ant Design Theme Override - NobleX Configuration
 * 
 * Customizes Ant Design components to match NobleX Industrial OS design system
 * 
 * @version 1.0.0
 * @date December 1, 2025
 */

import type { ThemeConfig } from 'antd';
import { NobleTokens } from './noble-tokens';

/**
 * NobleX Ant Design Theme Configuration
 */
export const nobleAntdTheme: ThemeConfig = {
  token: {
    // Color Palette
    colorPrimary: NobleTokens.colors.primary.commandBlue,
    colorSuccess: NobleTokens.colors.semantic.operationalGreen,
    colorWarning: NobleTokens.colors.semantic.warningAmber,
    colorError: NobleTokens.colors.semantic.criticalRed,
    colorInfo: NobleTokens.colors.semantic.infoBlue,
    
    // Text Colors
    colorText: NobleTokens.colors.text.primary,
    colorTextSecondary: NobleTokens.colors.text.secondary,
    colorTextDisabled: NobleTokens.colors.text.disabled,
    
    // Background Colors
    colorBgContainer: NobleTokens.colors.background.level1Light,
    colorBgElevated: NobleTokens.colors.background.level2Light,
    colorBgLayout: NobleTokens.colors.background.level0Light,
    
    // Border
    colorBorder: NobleTokens.colors.border.default,
    colorBorderSecondary: NobleTokens.colors.border.light,
    
    // Border Radius (increased from default)
    borderRadius: 8,
    borderRadiusLG: 12,
    borderRadiusSM: 4,
    
    // Font Family
    fontFamily: NobleTokens.typography.fontFamily.primary,
    fontSize: 15,
    fontSizeHeading1: 32,
    fontSizeHeading2: 24,
    fontSizeHeading3: 20,
    fontSizeHeading4: 16,
    fontSizeHeading5: 14,
    
    // Line Height
    lineHeight: 1.5715,
    lineHeightHeading1: 1.25,
    lineHeightHeading2: 1.35,
    lineHeightHeading3: 1.4,
    
    // Control Heights (increased for better touch targets)
    controlHeight: 48,
    controlHeightLG: 56,
    controlHeightSM: 40,
    
    // Spacing
    padding: 16,
    paddingLG: 24,
    paddingSM: 12,
    paddingXS: 8,
    paddingXXS: 4,
    
    margin: 16,
    marginLG: 24,
    marginSM: 12,
    marginXS: 8,
    marginXXS: 4,
    
    // Box Shadow (custom industrial shadows)
    boxShadow: NobleTokens.shadows.level1,
    boxShadowSecondary: NobleTokens.shadows.level2,
    
    // Motion
    motionDurationFast: '0.15s',
    motionDurationMid: '0.25s',
    motionDurationSlow: '0.35s',
    motionEaseInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    motionEaseOut: 'cubic-bezier(0, 0, 0.2, 1)',
    
    // Z-Index
    zIndexPopupBase: 1050,
  },
  
  components: {
    // Button Component
    Button: {
      primaryShadow: NobleTokens.shadows.level1,
      controlHeight: 48,
      controlHeightLG: 56,
      controlHeightSM: 40,
      paddingContentHorizontal: 24,
      fontWeight: 600,
      borderRadius: 8,
      
      // Primary button with gradient
      colorPrimary: NobleTokens.colors.primary.commandBlue,
      colorPrimaryHover: NobleTokens.colors.primary.commandBlueLight,
      colorPrimaryActive: NobleTokens.colors.primary.commandBlueDark,
      
      // Danger button
      colorError: NobleTokens.colors.semantic.criticalRed,
      colorErrorHover: NobleTokens.colors.semantic.criticalRedLight,
      colorErrorActive: NobleTokens.colors.semantic.criticalRedDark,
    },
    
    // Input Component
    Input: {
      controlHeight: 48,
      controlHeightLG: 56,
      controlHeightSM: 40,
      paddingBlock: 12,
      paddingInline: 16,
      borderRadius: 8,
      
      // Focus state
      activeShadow: NobleTokens.shadows.focusBlue,
      errorActiveShadow: NobleTokens.shadows.focusRed,
    },
    
    // Select Component
    Select: {
      controlHeight: 48,
      controlHeightLG: 56,
      controlHeightSM: 40,
      borderRadius: 8,
      optionPadding: '12px 16px',
    },
    
    // Card Component
    Card: {
      borderRadiusLG: 12,
      paddingLG: 24,
      boxShadowTertiary: NobleTokens.shadows.level1,
    },
    
    // Modal Component
    Modal: {
      borderRadiusLG: 16,
      paddingContentHorizontalLG: 24,
      paddingMD: 24,
    },
    
    // Table Component
    Table: {
      headerBg: '#F8FAFB',
      headerColor: NobleTokens.colors.text.primary,
      headerSortActiveBg: '#EDF2F7',
      borderColor: NobleTokens.colors.border.default,
      rowHoverBg: '#F8FAFB',
    },
    
    // Form Component
    Form: {
      labelFontSize: 14,
      labelColor: NobleTokens.colors.text.primary,
      labelHeight: 48,
      itemMarginBottom: 24,
    },
    
    // Tag/Badge Component
    Tag: {
      borderRadiusSM: 12,
      defaultBg: 'rgba(92, 107, 125, 0.08)',
      defaultColor: NobleTokens.colors.accent.steelGray,
    },
    
    // Menu Component
    Menu: {
      itemHeight: 48,
      itemPaddingInline: 16,
      itemBorderRadius: 8,
      itemActiveBg: 'rgba(20, 90, 84, 0.08)',
      itemSelectedBg: 'rgba(20, 90, 84, 0.08)',
      itemSelectedColor: NobleTokens.colors.primary.commandBlue,
    },
    
    // Layout Component
    Layout: {
      headerBg: '#FFFFFF',
      headerHeight: 64,
      headerPadding: '0 24px',
      siderBg: '#FFFFFF',
      bodyBg: NobleTokens.colors.background.level0Light,
    },
    
    // Notification Component
    Notification: {
      width: 360,
      paddingMD: 16,
      borderRadiusLG: 12,
    },
    
    // Message Component
    Message: {
      contentPadding: '12px 16px',
      borderRadiusLG: 8,
    },
    
    // Drawer Component
    Drawer: {
      paddingLG: 24,
    },
    
    // Tooltip Component
    Tooltip: {
      borderRadius: 8,
      paddingSM: 12,
    },
    
    // Progress Component
    Progress: {
      defaultColor: NobleTokens.colors.primary.commandBlue,
      remainingColor: 'rgba(0, 0, 0, 0.06)',
    },
    
    // Switch Component
    Switch: {
      trackHeight: 24,
      trackMinWidth: 48,
      handleSize: 20,
    },
    
    // Radio & Checkbox
    Radio: {
      size: 20,
      dotSize: 10,
    },
    
    Checkbox: {
      size: 20,
      borderRadiusSM: 4,
    },
    
    // DatePicker
    DatePicker: {
      controlHeight: 48,
      controlHeightLG: 56,
      controlHeightSM: 40,
      borderRadius: 8,
    },
    
    // Tabs
    Tabs: {
      itemActiveColor: NobleTokens.colors.primary.commandBlue,
      itemSelectedColor: NobleTokens.colors.primary.commandBlue,
      inkBarColor: NobleTokens.colors.primary.commandBlue,
      itemHoverColor: NobleTokens.colors.primary.commandBlueLight,
    },
    
    // Steps
    Steps: {
      iconSize: 32,
      dotSize: 12,
    },
    
    // Divider
    Divider: {
      colorSplit: NobleTokens.colors.border.default,
    },
    
    // Pagination
    Pagination: {
      itemSize: 40,
      itemSizeSM: 32,
    },
    
    // Breadcrumb
    Breadcrumb: {
      fontSize: 13,
      itemColor: NobleTokens.colors.text.secondary,
      lastItemColor: NobleTokens.colors.text.primary,
      linkColor: NobleTokens.colors.text.secondary,
      linkHoverColor: NobleTokens.colors.primary.commandBlue,
      separatorColor: NobleTokens.colors.text.disabled,
    },
    
    // Avatar
    Avatar: {
      containerSize: 40,
      containerSizeLG: 48,
      containerSizeSM: 32,
      borderRadius: 8,
    },
    
    // Badge
    Badge: {
      dotSize: 8,
      textFontSize: 11,
      textFontWeight: 600,
    },
    
    // Spin (Loading)
    Spin: {
      colorPrimary: NobleTokens.colors.primary.commandBlue,
      dotSize: 24,
      dotSizeLG: 40,
      dotSizeSM: 16,
    },
    
    // Alert
    Alert: {
      paddingContentHorizontalLG: 16,
      withDescriptionPadding: '16px 24px',
      borderRadiusLG: 12,
    },
  },
};

/**
 * Algorithm configuration for dark mode (future)
 */
export const nobleDarkTheme: ThemeConfig = {
  ...nobleAntdTheme,
  token: {
    ...nobleAntdTheme.token,
    colorBgContainer: NobleTokens.colors.background.level1Dark,
    colorBgElevated: NobleTokens.colors.background.level2Dark,
    colorBgLayout: NobleTokens.colors.background.level0Dark,
    colorText: NobleTokens.colors.text.primaryDark,
    colorTextSecondary: NobleTokens.colors.text.secondaryDark,
    colorTextDisabled: NobleTokens.colors.text.disabledDark,
  },
};

export default nobleAntdTheme;

